import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Search, Filter, Calendar, Building2, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: "status-draft",
  submitted: "status-submitted",
  under_review: "status-under_review",
  approved: "status-approved",
  rejected: "status-rejected",
};

const statusLabels = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under Review",
  approved: "Approved",
  rejected: "Rejected",
};

const initialFormState = {
  grant_name: "",
  organization: "",
  amount: "",
  deadline: "",
  status: "draft",
  notes: ""
};

export const GrantTrackerPage = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  const [formData, setFormData] = useState(initialFormState);

  const fetchGrants = async () => {
    try {
      const response = await axios.get(`${API}/grants`);
      setGrants(response.data);
    } catch (error) {
      toast.error("Failed to load grants");
      console.error("Fetch grants error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingGrant) {
        await axios.put(`${API}/grants/${editingGrant.id}`, payload);
        toast.success("Grant updated successfully");
      } else {
        await axios.post(`${API}/grants`, payload);
        toast.success("Grant added successfully");
      }
      
      setIsDialogOpen(false);
      setEditingGrant(null);
      setFormData(initialFormState);
      fetchGrants();
    } catch (error) {
      toast.error("Failed to save grant");
      console.error("Save grant error:", error);
    }
  };

  const handleEdit = (grant) => {
    setEditingGrant(grant);
    setFormData({
      grant_name: grant.grant_name,
      organization: grant.organization,
      amount: grant.amount.toString(),
      deadline: grant.deadline,
      status: grant.status,
      notes: grant.notes || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this grant?")) return;
    
    try {
      await axios.delete(`${API}/grants/${id}`);
      toast.success("Grant deleted successfully");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to delete grant");
      console.error("Delete grant error:", error);
    }
  };

  const openNewGrantDialog = () => {
    setEditingGrant(null);
    setFormData(initialFormState);
    setIsDialogOpen(true);
  };

  const filteredGrants = grants.filter(grant => {
    const matchesSearch = grant.grant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          grant.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || grant.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalAmount = grants.reduce((sum, g) => sum + g.amount, 0);
  const submittedCount = grants.filter(g => g.status !== "draft").length;
  const approvedAmount = grants.filter(g => g.status === "approved").reduce((sum, g) => sum + g.amount, 0);

  return (
    <div data-testid="grant-tracker-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-display">Grant Tracker</h1>
          <p className="text-slate-500 mt-1">Track and manage your grant applications</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-secondary" onClick={openNewGrantDialog} data-testid="add-grant-btn">
              <Plus size={18} className="mr-2" />
              Add Grant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="grant-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingGrant ? "Edit Grant" : "Add New Grant"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Grant Name</label>
                <Input
                  name="grant_name"
                  value={formData.grant_name}
                  onChange={handleChange}
                  placeholder="e.g., Small Business Grant 2024"
                  required
                  data-testid="grant-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Organization</label>
                <Input
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="e.g., SBA, Florida SBDC"
                  required
                  data-testid="grant-org-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Amount ($)</label>
                  <Input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="50000"
                    required
                    data-testid="grant-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Deadline</label>
                  <Input
                    type="date"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleChange}
                    required
                    data-testid="grant-deadline-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Status</label>
                <Select value={formData.status} onValueChange={(val) => handleSelectChange("status", val)}>
                  <SelectTrigger data-testid="grant-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Notes</label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes about this grant..."
                  className="min-h-[100px]"
                  data-testid="grant-notes-input"
                />
              </div>
              <DialogFooter className="gap-2">
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit" className="btn-primary" data-testid="grant-submit-btn">
                  {editingGrant ? "Update Grant" : "Add Grant"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="stat-card" data-testid="stat-total-grants">
          <DollarSign size={20} className="text-[#D4AF37] mb-2" />
          <p className="text-2xl font-bold text-[#0F172A]">${totalAmount.toLocaleString()}</p>
          <p className="text-sm text-slate-500">Total Grant Value</p>
        </div>
        <div className="stat-card" data-testid="stat-submitted">
          <Building2 size={20} className="text-[#D4AF37] mb-2" />
          <p className="text-2xl font-bold text-[#0F172A]">{submittedCount}</p>
          <p className="text-sm text-slate-500">Applications Submitted</p>
        </div>
        <div className="stat-card" data-testid="stat-approved">
          <Calendar size={20} className="text-[#D4AF37] mb-2" />
          <p className="text-2xl font-bold text-[#0F172A]">${approvedAmount.toLocaleString()}</p>
          <p className="text-sm text-slate-500">Approved Funding</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search grants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="grant-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-slate-400" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]" data-testid="grant-filter-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden" data-testid="grants-table">
        {loading ? (
          <div className="p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-slate-500">Loading grants...</p>
          </div>
        ) : filteredGrants.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-500 mb-4">No grants found</p>
            <Button onClick={openNewGrantDialog} variant="outline" data-testid="empty-add-grant-btn">
              <Plus size={18} className="mr-2" />
              Add Your First Grant
            </Button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Grant Name</th>
                <th>Organization</th>
                <th>Amount</th>
                <th>Deadline</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGrants.map((grant, index) => (
                <tr key={grant.id} data-testid={`grant-row-${index}`}>
                  <td>
                    <p className="font-medium text-[#0F172A]">{grant.grant_name}</p>
                    {grant.notes && (
                      <p className="text-xs text-slate-500 mt-1 truncate max-w-[200px]">{grant.notes}</p>
                    )}
                  </td>
                  <td className="text-slate-600">{grant.organization}</td>
                  <td className="font-semibold text-[#0F172A]">${grant.amount.toLocaleString()}</td>
                  <td className="text-slate-600">{new Date(grant.deadline).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge ${statusColors[grant.status]}`}>
                      {statusLabels[grant.status]}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEdit(grant)}
                        className="p-2 hover:bg-slate-100 rounded transition-colors"
                        data-testid={`edit-grant-${index}`}
                      >
                        <Edit2 size={16} className="text-slate-600" />
                      </button>
                      <button 
                        onClick={() => handleDelete(grant.id)}
                        className="p-2 hover:bg-red-50 rounded transition-colors"
                        data-testid={`delete-grant-${index}`}
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default GrantTrackerPage;
